"use client";

import {
  X,
  FileText,
  Download,
  Calendar,
  User,
  Book,
  Hash,
  DollarSign,
  FileImage,
  Info,
  Clock,
  Mail,
  Phone,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { returnDateWithTime } from "../../utils/functions/functions";
import { updatePaymentVerifiedAction } from "../../../services/action/common";
import { useState } from "react";

export function OrderDetailsModal({ leadDetails, onClose }) {
  const { user } = useSelector((state) => state.authenticationReducer);

  // Define user types

  const isExpert = user?.userType === "EXPERT";
  const isOperator = user?.userType === "OPERATOR";
  const isClient = user?.userType === "CLIENT";
  const isAccounts = user?.userType === "ACCOUNTS";
  const [verfiedPaymentLeadId,setVerifiedPaymentLeadId]=useState([])
  const isAdmin =
    user?.userType === "ADMIN" ||
    user?.userType === "SUPER_ADMIN" ||
    user?.userType === "MANAGER";

  // Format currency with proper symbol
  const formatCurrency = (amount, currency = "$") => {
    return `${currency} ${Number.parseFloat(amount).toFixed(2)}`;
  };

  // Get status badge color based on order status
  const getStatusColor = (status) => {
    switch (status) {
      case "ASSIGNED":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "COMPLETED":
        return "bg-green-100 text-green-800 border-green-200";
      case "CANCELLED":
        return "bg-red-100 text-red-800 border-red-200";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Extract file name from URL
  const getFileName = (url) => {
    if (!url) return "Document";
    const parts = url.split("/");
    return parts[parts.length - 1];
  };

  // Check if it's an image file
  const isImageFile = (url) => {
    const lowerCaseUrl = url.toLowerCase();
    return (
      lowerCaseUrl.endsWith(".jpg") ||
      lowerCaseUrl.endsWith(".jpeg") ||
      lowerCaseUrl.endsWith(".png") ||
      lowerCaseUrl.endsWith(".gif") ||
      lowerCaseUrl.endsWith(".webp")
    );
  };

  // Function to check if user has access to specific information
  const canViewSection = (section) => {
    switch (section) {
      case "verifiedBadge":
        return isAdmin || isOperator;
      case "clientInfo":
        return isAdmin || isOperator || isExpert || isAccounts;
      case "clientContactInfo":
        return isAdmin || isAccounts; // Only admin can see client contact details
      case "expertInfo":
        return isAdmin || isOperator || isClient || isAccounts;
      case "expertContactInfo":
        return isAdmin || isAccounts; // Only admin can see expert contact details
      case "createdByInfo":
        return isAdmin || isOperator;
      case "createdByContactInfo":
        return isAdmin || isAccounts; // Only admin can see creator contact details
      case "financialDetails":
        return true; // Everyone can see financial details, but specific items are filtered
      case "expertFinancials":
        return isAdmin || isOperator || isExpert || isAccounts;
      case "clientFinancials":
        return isAdmin || isOperator || isClient || isAccounts;
      case "requirements":
        return isAdmin || isOperator || isExpert;
      case "documents":
        return true; // Everyone can see documents
      default:
        return true;
    }
  };

  const dispatch = useDispatch();
  const handleVerifyPayment = () => {
    dispatch(
      updatePaymentVerifiedAction({
        data: {
          leadId: leadDetails?._id,
        },
        cb:()=>{
          setVerifiedPaymentLeadId((prev)=>[...prev,leadDetails?._id])
        }
      })
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 transition-opacity duration-300">
      <div className="bg-white rounded-lg shadow-2xl max-h-[90vh] overflow-hidden w-[650px] max-w-[95vw] animate-fadeIn">
        {/* Header */}
        <div className="flex justify-between items-center px-4 py-3 border-b bg-[#0f3a6b]">
          <div className="flex items-center gap-2">
            <div className="bg-primary/10 p-1.5 rounded-full">
              <Info className="w-6 h-6 mb-2 text-white" />
            </div>
            <h3 className="text-white font-semibold">
              Order No : {leadDetails?.orderId}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="overflow-y-auto max-h-[calc(90vh-48px)] p-3">
          <div className="space-y-3">
            {/* Order ID and Status Section */}
            <div className="bg-white p-3 rounded-lg border shadow-sm">
              <div className="flex flex-wrap justify-between gap-2 items-center">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <Hash className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-xs font-medium text-gray-500">
                      Order ID
                    </span>
                  </div>
                  <span className="font-medium text-gray-800 text-sm">
                    {leadDetails?.orderId || "N/A"}
                  </span>
                </div>
                <div>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                      leadDetails?.orderStatus
                    )}`}
                  >
                    {leadDetails?.orderStatus || "Open"}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-xs font-medium text-gray-500">
                      Created On
                    </span>
                  </div>
                  <p className="text-xs text-gray-800">
                    {returnDateWithTime(leadDetails?.createdAt) || "N/A"}
                  </p>
                </div>

                {leadDetails?.createdBy && canViewSection("createdByInfo") && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-gray-500" />
                      <span className="text-xs font-medium text-gray-500">
                        Created By
                      </span>
                    </div>
                    <p className="text-xs text-gray-800">
                      {leadDetails.createdBy.name}
                      <span className="text-xs ml-1 text-gray-500">
                        ({leadDetails.createdBy.userType})
                      </span>
                    </p>

                    {canViewSection("createdByContactInfo") && (
                      <div className="mt-1 space-y-1">
                        {leadDetails.createdBy.email && (
                          <div className="flex items-center gap-1.5 text-xs text-gray-600">
                            <Mail className="w-3 h-3 text-gray-400" />
                            <span>{leadDetails.createdBy.email}</span>
                          </div>
                        )}
                        {leadDetails.createdBy.number && (
                          <div className="flex items-center gap-1.5 text-xs text-gray-600">
                            <Phone className="w-3 h-3 text-gray-400" />
                            <span>{leadDetails.createdBy.number}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Financial Information */}
            <div className="bg-white p-3 rounded-lg border shadow-sm">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="bg-emerald-100 p-1 rounded-full">
                  <DollarSign className="w-3 h-3 text-emerald-600" />
                </div>
                <h4 className="text-xs font-medium text-gray-700">
                  Financial Details
                </h4>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2 pl-1">
                {canViewSection("clientFinancials") && (
                  <>
                    {leadDetails?.totalPayment !== undefined && (
                      <div className="space-y-0.5">
                        <span className="text-xs font-medium text-gray-500">
                          Total Payment
                        </span>
                        <p className="text-sm font-medium text-gray-800">
                          {formatCurrency(
                            leadDetails.totalPayment,
                            leadDetails?.currancy || "$"
                          )}
                        </p>
                      </div>
                    )}

                    {leadDetails?.paymentRecieved !== undefined && (
                      <div className="space-y-0.5">
                        <span className="text-xs font-medium text-gray-500">
                          Payment Received
                        </span>
                        <p className="text-sm font-medium text-gray-800">
                          {formatCurrency(
                            leadDetails.paymentRecieved,
                            leadDetails?.currancy || "$"
                          )}
                        </p>
                      </div>
                    )}
                  </>
                )}

                {canViewSection("expertFinancials") && (
                  <>
                    {leadDetails?.expertAmount !== undefined && (
                      <div className="space-y-0.5">
                        <span className="text-xs font-medium text-gray-500">
                          Expert Amount
                        </span>
                        <p className="text-sm font-medium text-gray-800">
                          ₹ {leadDetails.expertAmount}
                        </p>
                      </div>
                    )}

                    {leadDetails?.penality?.amount !== undefined && (
                      <div className="space-y-0.5">
                        <span className="text-xs font-medium text-gray-500">
                          Expert Penalty
                        </span>
                        <p
                          className={`text-sm font-medium ${
                            leadDetails.penality.amount > 0
                              ? "text-red-600"
                              : "text-gray-800"
                          }`}
                        >
                          ₹ {leadDetails.penality.amount}
                          {leadDetails.penality.reason &&
                            leadDetails.penality.amount > 0 && (
                              <span className="block text-xs text-gray-500 mt-0.5">
                                Reason: {leadDetails.penality.reason}
                              </span>
                            )}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
              {/* Payment Method Section */}
              {canViewSection("clientFinancials") &&
                leadDetails?.paymentMethod && (
                  <div className="col-span-2 space-y-1 mt-2 pt-2 border-t">
                    <span className="text-xs font-medium text-gray-500">
                      Payment Method
                    </span>
                    <div className="flex items-center gap-2">
                      {leadDetails.paymentMethod === "STRIPE" && (
                        <div className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium flex items-center gap-1.5">
                          <DollarSign className="w-3 h-3" />
                          Stripe
                        </div>
                      )}
                      {leadDetails.paymentMethod === "RAZOR_PAY" && (
                        <div className="px-2 py-1 bg-teal-50 text-teal-700 rounded-md text-xs font-medium flex items-center gap-1.5">
                          <DollarSign className="w-3 h-3" />
                          Razor Pay
                        </div>
                      )}
                      {!["STRIPE", "RAZOR_PAY"].includes(
                        leadDetails.paymentMethod
                      ) && (
                        <div className="px-2 py-1 bg-gray-50 text-gray-700 rounded-md text-xs font-medium flex items-center gap-1.5">
                          <DollarSign className="w-3 h-3" />
                          {leadDetails.paymentMethod || "Standard"}
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>
            {isAccounts && (
              <div className="col-span-2 mt-3 pt-2 border-t">
                <button
                  disabled={leadDetails?.paymentVerified || verfiedPaymentLeadId?.includes(leadDetails?._id)}
                  onClick={handleVerifyPayment}
                  className="w-full py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <DollarSign className="w-4 h-4" />
                  Payment Verification:{" "}
                  {leadDetails?.paymentVerified || verfiedPaymentLeadId?.includes(leadDetails?._id) ? "Verfied" : "Verify Payment"}
                </button>
              </div>
            )}

            {canViewSection("verifiedBadge") && (
              <div className="col-span-2 mt-3 pt-2 border-t">
                <button
                  disabled={leadDetails?.paymentVerified}
                  className="w-full py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <DollarSign className="w-4 h-4" />
                  Payment Verification:{" "}
                  {leadDetails?.paymentVerified ? "Verfied" : "Pending"}
                </button>
              </div>
            )}
            {/* Client Information - Visible to Admin, Operator, Expert */}
            {canViewSection("clientInfo") && (
              <div className="bg-white p-3 rounded-lg border shadow-sm">
                <div className="flex items-center gap-1.5 mb-2">
                  <div className="bg-blue-100 p-1 rounded-full">
                    <User className="w-3 h-3 text-blue-600" />
                  </div>
                  <h4 className="text-xs font-medium text-gray-700">
                    Client Information
                  </h4>
                </div>
                <div className="space-y-2 pl-1">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div className="space-y-0.5">
                      <span className="text-xs font-medium text-gray-500">
                        Name
                      </span>
                      <p className="text-sm text-gray-800">
                        {leadDetails?.client?.name || "N/A"}
                      </p>
                    </div>

                    <div className="space-y-0.5">
                      <span className="text-xs font-medium text-gray-500">
                        User ID
                      </span>
                      <p className="text-sm text-gray-800">
                        {leadDetails?.client?.userId || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Contact information - only visible to admin */}
                  {canViewSection("clientContactInfo") && (
                    <div className="pt-1 border-t mt-2">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        {leadDetails?.client?.email && (
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1.5">
                              <Mail className="w-3 h-3 text-gray-400" />
                              <span className="text-xs font-medium text-gray-500">
                                Email
                              </span>
                            </div>
                            <p className="text-xs text-gray-800">
                              {leadDetails.client.email}
                            </p>
                          </div>
                        )}

                        {leadDetails?.client?.number && (
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1.5">
                              <Phone className="w-3 h-3 text-gray-400" />
                              <span className="text-xs font-medium text-gray-500">
                                Phone
                              </span>
                            </div>
                            <p className="text-xs text-gray-800">
                              {leadDetails.client.number}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Expert Information - Visible to Admin, Operator, Client */}
            {canViewSection("expertInfo") && (
              <div className="bg-white p-3 rounded-lg border shadow-sm">
                <div className="flex items-center gap-1.5 mb-2">
                  <div className="bg-green-100 p-1 rounded-full">
                    <User className="w-3 h-3 text-green-600" />
                  </div>
                  <h4 className="text-xs font-medium text-gray-700">
                    Expert Information
                  </h4>
                </div>
                <div className="space-y-2 pl-1">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    {isAdmin || isOperator ? (
                      <div className="space-y-0.5">
                        <span className="text-xs font-medium text-gray-500">
                          Name
                        </span>
                        <p className="text-sm text-gray-800">
                          {leadDetails?.expert?.name || "N/A"}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-0.5">
                        <span className="text-xs font-medium text-gray-500">
                          Expert ID
                        </span>
                        <p className="text-sm text-gray-800">
                          {leadDetails?.expert?.userId || "N/A"}
                        </p>
                      </div>
                    )}

                    {isAdmin || isOperator ? (
                      <div className="space-y-0.5">
                        <span className="text-xs font-medium text-gray-500">
                          User ID
                        </span>
                        <p className="text-sm text-gray-800">
                          {leadDetails?.expert?.userId || "N/A"}
                        </p>
                      </div>
                    ) : null}
                  </div>

                  {/* Contact information - only visible to admin */}
                  {canViewSection("expertContactInfo") && (
                    <div className="pt-1 border-t mt-2">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        {leadDetails?.expert?.email && (
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1.5">
                              <Mail className="w-3 h-3 text-gray-400" />
                              <span className="text-xs font-medium text-gray-500">
                                Email
                              </span>
                            </div>
                            <p className="text-xs text-gray-800">
                              {leadDetails.expert.email}
                            </p>
                          </div>
                        )}

                        {leadDetails?.expert?.number && (
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1.5">
                              <Phone className="w-3 h-3 text-gray-400" />
                              <span className="text-xs font-medium text-gray-500">
                                Phone
                              </span>
                            </div>
                            <p className="text-xs text-gray-800">
                              {leadDetails.expert.number}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Order Details Section */}
            <div className="bg-white p-3 rounded-lg border shadow-sm">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="bg-purple-100 p-1 rounded-full">
                  <Book className="w-3 h-3 text-purple-600" />
                </div>
                <h4 className="text-xs font-medium text-gray-700">
                  Order Details
                </h4>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2 pl-1">
                <div className="space-y-0.5">
                  <span className="text-xs font-medium text-gray-500">
                    Subject
                  </span>
                  <p className="text-sm text-gray-800">
                    {leadDetails?.subject || "N/A"}
                  </p>
                </div>

                <div className="space-y-0.5">
                  <span className="text-xs font-medium text-gray-500">
                    Word Count
                  </span>
                  <p className="text-sm text-gray-800">
                    {leadDetails?.wordCount?.toLocaleString() || "N/A"}
                  </p>
                </div>

                {leadDetails?.referenceType && (
                  <div className="space-y-0.5">
                    <span className="text-xs font-medium text-gray-500">
                      Referencing Style
                    </span>
                    <p className="text-sm text-gray-800">
                      {leadDetails.referenceType}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Deadlines Section */}
            <div className="bg-white p-3 rounded-lg border shadow-sm">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="bg-amber-100 p-1 rounded-full">
                  <Calendar className="w-3 h-3 text-amber-600" />
                </div>
                <h4 className="text-xs font-medium text-gray-700">Deadlines</h4>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2 pl-1">
                {(isAdmin || isOperator || isClient) && (
                  <div className="space-y-0.5">
                    <span className="text-xs font-medium text-gray-500">
                      Client Deadline
                    </span>
                    <p className="text-sm text-gray-800">
                      {returnDateWithTime(leadDetails?.clientDeadline) || "N/A"}
                    </p>
                  </div>
                )}

                {(isAdmin || isOperator || isExpert) && (
                  <div className="space-y-0.5">
                    <span className="text-xs font-medium text-gray-500">
                      Expert Deadline
                    </span>
                    <p className="text-sm text-gray-800">
                      {returnDateWithTime(leadDetails?.expertDeadline) || "N/A"}
                    </p>
                  </div>
                )}

                {leadDetails?.reopenDeadline && (
                  <div className="space-y-0.5">
                    <span className="text-xs font-medium text-gray-500">
                      Reopen Deadline
                    </span>
                    <p className="text-sm text-gray-800">
                      {returnDateWithTime(leadDetails.reopenDeadline) || "N/A"}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Requirements Section - Visible to Admin, Operator, Expert */}
            {canViewSection("requirements") && leadDetails?.requirement && (
              <div className="bg-white p-3 rounded-lg border shadow-sm">
                <div className="flex items-center gap-1.5 mb-2">
                  <div className="bg-indigo-100 p-1 rounded-full">
                    <Info className="w-3 h-3 text-indigo-600" />
                  </div>
                  <h4 className="text-xs font-medium text-gray-700">
                    Requirements
                  </h4>
                </div>

                <div
                  className="p-2 bg-gray-50 rounded-lg text-gray-800 prose prose-sm max-w-none border border-gray-100 text-xs"
                  dangerouslySetInnerHTML={{ __html: leadDetails.requirement }}
                />
              </div>
            )}

            {/* Documents Section - Visible to all */}
            {canViewSection("documents") &&
              leadDetails?.document &&
              leadDetails.document.length > 0 && (
                <div className="bg-white p-3 rounded-lg border shadow-sm">
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className="bg-rose-100 p-1 rounded-full">
                      <FileText className="w-3 h-3 text-rose-600" />
                    </div>
                    <h4 className="text-xs font-medium text-gray-700">
                      Documents
                    </h4>
                  </div>

                  <div className="grid grid-cols-1 gap-2">
                    {leadDetails.document.map((doc, index) => (
                      <a
                        key={index}
                        href={doc}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-100 group"
                      >
                        <div className="bg-blue-50 p-1 rounded-md border border-blue-100 group-hover:bg-blue-100 transition-colors">
                          <FileText className="w-3 h-3 text-blue-600" />
                        </div>
                        <span className="text-xs text-gray-700 flex-1 truncate">
                          {getFileName(doc)}
                        </span>
                        <div className="bg-gray-100 p-1 rounded-md group-hover:bg-gray-200 transition-colors">
                          <Download className="w-3 h-3 text-gray-600" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

            {/* Screenshots Section - Visible to all */}
            {canViewSection("documents") &&
              leadDetails?.screenshots &&
              leadDetails.screenshots.length > 0 && (
                <div className="bg-white p-3 rounded-lg border shadow-sm">
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className="bg-cyan-100 p-1 rounded-full">
                      <FileImage className="w-3 h-3 text-cyan-600" />
                    </div>
                    <h4 className="text-xs font-medium text-gray-700">
                      Screenshots
                    </h4>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {leadDetails.screenshots.map((screenshot, index) => (
                      <div
                        key={index}
                        className="group relative overflow-hidden rounded-md border border-gray-200 shadow-sm"
                      >
                        {isImageFile(screenshot) ? (
                          <a
                            href={screenshot}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={screenshot || "/placeholder.svg"}
                              alt={`Screenshot ${index + 1}`}
                              className="w-full h-20 object-cover transition-transform group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <div className="bg-white p-1 rounded-full">
                                <Download className="w-3 h-3 text-gray-800" />
                              </div>
                            </div>
                          </a>
                        ) : (
                          <a
                            href={screenshot}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex flex-col items-center justify-center h-20 bg-gray-50 p-2 hover:bg-gray-100 transition-colors"
                          >
                            <FileImage className="w-4 h-4 text-gray-400 mb-1" />
                            <span className="text-xs text-gray-600 text-center truncate w-full">
                              {getFileName(screenshot)}
                            </span>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
